import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { detectDevice } from '../Utlils';
import { useGlobalContext } from '../Contexts/GlobalContext';

const DesktopIcon = () => (
  <svg
    className="w-20 h-20 text-blue-500"
    fill="currentColor"
    viewBox="0 0 24 24"
  >
    <path d="M21 2H3C1.9 2 1 2.9 1 4V16C1 17.1 1.9 18 3 18H10V20H8V22H16V20H14V18H21C22.1 18 23 17.1 23 16V4C23 2.9 22.1 2 21 2ZM21 16H3V4H21V16Z" />
  </svg>
);
const MobileIcon = () => (
  <svg
    className="w-16 h-20 text-blue-500"
    fill="currentColor"
    viewBox="0 0 24 24"
  >
    <path d="M15.5 1H8.5C6.567 1 5 2.567 5 4.5V19.5C5 21.433 6.567 23 8.5 23H15.5C17.433 23 19 21.433 19 19.5V4.5C19 2.567 17.433 1 15.5 1ZM12 21C11.448 21 11 20.552 11 20C11 19.448 11.448 19 12 19C12.552 19 13 19.448 13 20C13 20.552 12.552 21 12 21ZM17 17.5H7V4.5H17V17.5Z" />
  </svg>
);
const TabletIcon = () => (
  <svg
    className="w-20 h-20 text-blue-500"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <circle cx="12" cy="18.5" r="0.8" fill="currentColor" />
  </svg>
);

const DEVICES = {
  desktop: {
    label: 'desktop',
    Icon: DesktopIcon,
    headline: 'Optimized for Desktop.',
  },
  tablet: {
    label: 'tablet',
    Icon: TabletIcon,
    headline: 'Optimized for Tablet.',
  },
  mobile: {
    label: 'mobile device',
    Icon: MobileIcon,
    headline: 'Designed for Mobile.',
  },
};
const PRIORITY = ['desktop', 'tablet', 'mobile'];

const joinLabels = (list) => {
  const labels = list.map((d) => DEVICES[d].label);
  return labels.length > 1
    ? `${labels.slice(0, -1).join(', ')} or ${labels.at(-1)}`
    : labels[0];
};

export const DeviceRestrictionPage = ({
  allowed = [],
  url = window.location.href,
  onClose,
}) => {
  const { setSnackbar } = useGlobalContext();
  const known = PRIORITY.filter((d) => allowed.includes(d));
  const primary = known[0];
  const showQr = known.some((d) => d !== 'desktop');
  const wide = !known.includes('mobile') === false ? false : true;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setSnackbar({
        open: true,
        msg: 'URL copied to clipboard!',
        severity: 'success',
      });
    } catch {
      window.prompt('Copy this URL:', url);
    }
  };
  const canShare = typeof navigator !== 'undefined' && !!navigator.share;

  const share = async () => {
    try {
      await navigator.share({
        title: 'Open this link on your ' + joinLabels(known),
        text: `Please open this link on your ${joinLabels(known)}:`,
        url,
      });
    } catch (err) {
      if (err.name !== 'AbortError') copy();
    }
  };

  // No device allowed at all -> generic "unavailable" state
  if (!primary) {
    return (
      <Shell onClose={onClose}>
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Not available</h1>
        <p className="text-gray-600">
          This content isn&apos;t available on your device right now.
        </p>
      </Shell>
    );
  }

  const { Icon, headline } = DEVICES[primary];
  const title = known.length > 1 ? `Open on a ${joinLabels(known)}.` : headline;

  return (
    <Shell onClose={onClose} wide={showQr}>
      <div className="w-32 h-32 bg-sky-100 rounded-full flex items-center justify-center mb-6">
        <Icon />
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-3">{title}</h1>
      <p className="text-gray-600 mb-8 max-w-sm">
        This content isn&apos;t supported on your current device. For full
        functionality, please open this link on your {joinLabels(known)}.
      </p>

      <div
        className={`flex items-center justify-center gap-6 w-full ${showQr ? 'sm:flex-row flex-col' : 'flex-col'}`}
      >
        <div className="w-full max-w-sm flex flex-col items-center">
          <div className="flex items-center bg-[#1e293b] rounded-lg p-1 w-full mb-3 shadow-inner">
            <span className="flex-1 text-sm text-gray-300 truncate px-3 text-left">
              {url}
            </span>
            <button
              onClick={copy}
              className="bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold py-2 px-4 rounded-md transition shadow"
            >
              Copy URL
            </button>
          </div>
          {canShare && (
            <button
              onClick={share}
              className="w-full flex items-center justify-center gap-2 border-2 border-blue-500 text-blue-600 hover:bg-blue-50 font-semibold py-2.5 rounded-lg transition mb-3"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M16 6l-4-4-4 4M12 2v13"
                />
              </svg>
              Share link
            </button>
          )}
          <p className="text-sm text-gray-500 font-medium">
            Paste this link into your {joinLabels(known)} browser.
          </p>
        </div>

        {showQr && (
          <div className="flex items-center gap-4 text-left sm:border-l-2 border-gray-100 sm:pl-6">
            <div className="bg-white border border-gray-200 rounded-lg p-1 shadow-sm">
              <QRCodeSVG value={url} size={80} />
            </div>
            <p className="text-sm text-gray-600 max-w-[120px]">
              Or scan this QR code with your phone camera.
            </p>
          </div>
        )}
      </div>
    </Shell>
  );
};

const Shell = ({ children, onClose, wide }) => (
  <div className="min-h-screen bg-sky-400 flex items-center justify-center p-4 font-sans">
    <div
      className={`bg-white rounded-[2rem] shadow-2xl relative w-full p-8 ${wide ? 'max-w-2xl sm:p-12' : 'max-w-sm'}`}
    >
      {onClose && (
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 bg-gray-100 rounded-full p-2 transition"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
      <div className="flex flex-col items-center text-center">{children}</div>
    </div>
  </div>
);

/* ---------- 4. The gate: fetch config -> compare with current device -> render page or children ---------- */
/**
 * <DeviceGate endpoint="/api/device-access?tool=special-tool">
 *   <YourActualTool />
 * </DeviceGate>
 *
 * Endpoint must return: { deviceType: { desktop: true, mobile: false, tablet: false } }
 */
export const DeviceGate = ({
  endpoint,
  children,
  fallbackOnError = 'allow',
}) => {
  const current = detectDevice();
  console.log('current=>', current);

  return <DeviceRestrictionPage allowed={['mobile', 'tablet']} />;
};

export default DeviceGate;
